import { useState, useRef, useCallback, useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import FeaturedBeats from './components/FeaturedBeats';
import Features from './components/Features';
import ServicesSection from './components/ServicesSection';
import BookingSection from './components/BookingSection';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import BundleDeals from './components/BundleDeals';
import NowPlayingBar from './components/NowPlayingBar';
import CartDrawer from './components/CartDrawer';
import FloatingCartButton from './components/FloatingCartButton';
import { mockBeats } from '../../mocks/beats';
import { CartBeat } from '../../hooks/useCartDiscount';

interface Beat {
  id: string;
  title: string;
  price: number;
  licenseType: string;
  isFree: boolean;
  sold: boolean;
  bpm: number;
  key: string;
  genre: string[];
  audioUrl: string;
  downloadUrl?: string;
  waveformImage: string;
}

export default function HomePage() {
  const [currentBeat, setCurrentBeat] = useState<Beat | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [cartItems, setCartItems] = useState<CartBeat[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [activeGenre, setActiveGenre] = useState('All');

  const audioRef = useRef<HTMLAudioElement>(null);

  // Handle ?beat=<id> URL param — auto-select beat from a shared link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const beatId = params.get('beat');
    if (!beatId) return;

    const found = mockBeats.find(b => b.id === beatId) as Beat | undefined;
    if (!found) return;

    setCurrentBeat(found);
    if (audioRef.current) {
      audioRef.current.src = found.audioUrl;
      audioRef.current.load();
      audioRef.current.volume = volume;
    }

    // Scroll to beats section after paint
    const timer = setTimeout(() => {
      const el = document.getElementById('beats');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 600);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredBeats = (activeGenre === 'All'
    ? mockBeats
    : mockBeats.filter(b => b.genre.includes(activeGenre))) as Beat[];

  const handleAddToCart = useCallback((beat: Beat) => {
    setCartItems(prev => {
      if (prev.find(i => i.id === beat.id)) return prev;
      return [...prev, beat as CartBeat];
    });
    // Don't auto-open cart — floating button + toast give the feedback
  }, []);

  const handleRemoveFromCart = useCallback((id: string) => {
    setCartItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const playAudio = () => {
    audioRef.current?.play().catch(() => {});
  };

  const handlePlayBeat = (beat: Beat) => {
    if (currentBeat?.id === beat.id) {
      setIsPlaying(prev => {
        if (prev) audioRef.current?.pause();
        else playAudio();
        return !prev;
      });
    } else {
      setCurrentBeat(beat);
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(true);
      if (audioRef.current) {
        audioRef.current.src = beat.audioUrl;
        audioRef.current.load();
        audioRef.current.volume = volume;
        audioRef.current.play().catch(() => {});
      }
    }
  };

  const handlePrev = useCallback(() => {
    const beats = filteredBeats;
    const idx = currentBeat ? beats.findIndex(b => b.id === currentBeat.id) : 0;
    const prevIdx = idx <= 0 ? beats.length - 1 : idx - 1;
    const prev = beats[prevIdx];
    if (!prev) return;
    setCurrentBeat(prev);
    setCurrentTime(0);
    setIsPlaying(true);
    if (audioRef.current) {
      audioRef.current.src = prev.audioUrl;
      audioRef.current.load();
      audioRef.current.volume = volume;
      audioRef.current.play().catch(() => {});
    }
  }, [currentBeat, volume, filteredBeats]);

  const handleNext = useCallback(() => {
    const beats = filteredBeats;
    const idx = currentBeat ? beats.findIndex(b => b.id === currentBeat.id) : 0;
    const nextIdx = (idx + 1) % beats.length;
    const next = beats[nextIdx];
    if (!next) return;
    setCurrentBeat(next);
    setCurrentTime(0);
    setIsPlaying(true);
    if (audioRef.current) {
      audioRef.current.src = next.audioUrl;
      audioRef.current.load();
      audioRef.current.volume = volume;
      audioRef.current.play().catch(() => {});
    }
  }, [currentBeat, volume, filteredBeats]);

  const handleSeek = (time: number) => {
    setCurrentTime(time);
    if (audioRef.current) audioRef.current.currentTime = time;
  };

  const handleVolumeChange = (vol: number) => {
    setVolume(vol);
    if (audioRef.current) audioRef.current.volume = vol;
  };

  const handlePlayPause = () => {
    setIsPlaying(prev => {
      if (prev) audioRef.current?.pause();
      else playAudio();
      return !prev;
    });
  };

  const cartItemIds = new Set(cartItems.map(i => i.id));

  return (
    <div className={`min-h-screen bg-white transition-all ${currentBeat ? 'pb-20' : ''}`}>
      <audio
        ref={audioRef}
        onTimeUpdate={() => {
          if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
        }}
        onLoadedMetadata={() => {
          if (audioRef.current) setDuration(audioRef.current.duration);
        }}
        onEnded={handleNext}
      />

      <Navbar
        cartCount={cartItems.length}
        onCartOpen={() => setIsCartOpen(true)}
      />
      <Hero />
      <FeaturedBeats
        onPlayBeat={handlePlayBeat}
        currentBeat={currentBeat}
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        volume={volume}
        onSeek={handleSeek}
        onVolumeChange={handleVolumeChange}
        onPrev={handlePrev}
        onNext={handleNext}
        onAddToCart={handleAddToCart}
        onRemoveFromCart={handleRemoveFromCart}
        cartItemIds={cartItemIds}
        activeGenre={activeGenre}
        onGenreChange={setActiveGenre}
        filteredBeats={filteredBeats}
      />
      <BundleDeals />
      <Features />
      <ServicesSection />
      <BookingSection />
      <CTASection />
      <Footer />

      <NowPlayingBar
        currentBeat={currentBeat}
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        volume={volume}
        onPlayPause={handlePlayPause}
        onPrev={handlePrev}
        onNext={handleNext}
        onSeek={handleSeek}
        onVolumeChange={handleVolumeChange}
      />

      <FloatingCartButton
        cartItems={cartItems}
        onCartOpen={() => setIsCartOpen(true)}
        hasNowPlaying={!!currentBeat}
      />

      <CartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        onRemoveItem={handleRemoveFromCart}
      />
    </div>
  );
}
